import React, { createContext, useContext, useState, useEffect } from 'react';
import { updateConsent, trackEvent as gaTrackEvent, trackPageView as gaTrackPageView } from '../utils/analytics';

const PrivacyContext = createContext();

export const usePrivacy = () => {
  const context = useContext(PrivacyContext);
  if (!context) {
    throw new Error('usePrivacy must be used within a PrivacyProvider');
  }
  return context;
};

export const PrivacyProvider = ({ children }) => {
  const [cookieConsent, setCookieConsent] = useState(null);
  const [isConsentLoaded, setIsConsentLoaded] = useState(false);

  useEffect(() => {
    // Load saved consent from localStorage
    const savedConsent = localStorage.getItem('aurelia-cookie-consent');
    if (savedConsent) {
      try {
        const parsedConsent = JSON.parse(savedConsent);
        setCookieConsent(parsedConsent);
        initializeTracking(parsedConsent);
      } catch (error) {
        console.error('Error parsing cookie consent:', error);
        localStorage.removeItem('aurelia-cookie-consent');
      }
    }
    setIsConsentLoaded(true);
  }, []);

  const initializeTracking = (consent) => {
    // Update Google Analytics consent
    updateConsent(consent);

    // Initialize preference cookies
    if (consent.preferences) {
      // Set user preference cookies
      document.cookie = "user_preferences=enabled; path=/; max-age=31536000; SameSite=Lax";
    }
  };

  const updateCookieConsent = (newConsent) => {
    setCookieConsent(newConsent);
    localStorage.setItem('aurelia-cookie-consent', JSON.stringify(newConsent));
    initializeTracking(newConsent);
  };

  const clearCookieConsent = () => {
    setCookieConsent(null);
    localStorage.removeItem('aurelia-cookie-consent');
    
    // Clear all non-necessary cookies
    updateConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    });
    
    // Clear preference cookies
    document.cookie = "user_preferences=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  };

  const hasConsent = (type) => {
    if (!cookieConsent) return false;
    return cookieConsent[type] === true;
  };

  const trackEvent = (eventName, parameters = {}) => {
    if (hasConsent('analytics')) {
      gaTrackEvent(eventName, parameters);
    }
  };

  const trackPageView = (pagePath, pageTitle) => {
    if (hasConsent('analytics')) {
      gaTrackPageView(pagePath, pageTitle);
    }
  };

  const setUserPreference = (key, value) => {
    if (hasConsent('preferences')) {
      localStorage.setItem(`aurelia_pref_${key}`, JSON.stringify(value));
    }
  };

  const getUserPreference = (key, defaultValue = null) => {
    if (!hasConsent('preferences')) return defaultValue;
    
    try {
      const saved = localStorage.getItem(`aurelia_pref_${key}`);
      return saved ? JSON.parse(saved) : defaultValue;
    } catch (error) {
      console.error('Error reading user preference:', error);
      return defaultValue;
    }
  };

  const value = {
    cookieConsent,
    isConsentLoaded,
    updateCookieConsent,
    clearCookieConsent,
    hasConsent,
    trackEvent,
    trackPageView,
    setUserPreference,
    getUserPreference
  };

  return (
    <PrivacyContext.Provider value={value}>
      {children}
    </PrivacyContext.Provider>
  );
};